import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';

export interface ProjectRow {
  id: string;
  name: string;
  status: 'ideation' | 'in_progress' | 'completed' | 'paused' | null;
  problem: string | null;
  target_audience: string | null;
  data_used: string | null;
  solution_description: string | null;
  solution_type: number | null;
  kpi_name: string | null;
  kpi_current_value: string | null;
  kpi_target_value: string | null;
  measurement_frequency: string | null;
  submitted: boolean | null;
  submitted_at: string | null;
  created_at: string;
  cohort_id: string | null;
  user_id: string;
  rubric_answers: Record<string, string> | null;
  is_org_visible: boolean;
  is_public: boolean;
  file_url: string | null;
  file_urls: any;
  roi_data: Record<string, any> | null;
}

// Map solution_type int to string key
const SOLUTION_TYPE_MAP: Record<number, string> = {
  1: 'content',
  2: 'vibecoding',
  3: 'assistant',
  4: 'automation',
  5: 'data',
};

export const solutionTypeToKey = (val: number | null): string => SOLUTION_TYPE_MAP[val ?? 0] || 'assistant';
export const solutionKeyToInt = (key: string): number => {
  const entry = Object.entries(SOLUTION_TYPE_MAP).find(([, v]) => v === key);
  return entry ? parseInt(entry[0]) : 3;
};

export function useProjects() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['projects', user?.id],
    queryFn: async () => {
      if (!user) return [];
      // Trae proyectos propios + proyectos donde es colaborador
      const [ownedRes, collabIdsRes] = await Promise.all([
        supabase
          .from('participant_projects')
          .select('*')
          .eq('user_id', user.id),
        supabase
          .from('project_collaborators')
          .select('project_id')
          .eq('user_id', user.id),
      ]);
      if (ownedRes.error) throw ownedRes.error;
      if (collabIdsRes.error) throw collabIdsRes.error;

      const ownedProjects = (ownedRes.data ?? []) as ProjectRow[];
      const collabProjectIds = (collabIdsRes.data ?? [])
        .map((r: any) => r.project_id)
        .filter((id: string) => !ownedProjects.some(p => p.id === id));

      let collabProjects: ProjectRow[] = [];
      if (collabProjectIds.length > 0) {
        const { data: cpData, error: cpErr } = await supabase
          .from('participant_projects')
          .select('*')
          .in('id', collabProjectIds);
        if (cpErr) throw cpErr;
        collabProjects = (cpData ?? []) as ProjectRow[];
      }

      const all = [...ownedProjects, ...collabProjects];
      all.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
      return all;
    },
    enabled: !!user,
  });
}

export function useProject(projectId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const { data, error } = await supabase
        .from('participant_projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
    enabled: !!user && !!projectId,
  });
}

export function useProjectTools(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-tools', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const { data, error } = await supabase
        .from('project_tools')
        .select('*')
        .eq('project_id', projectId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!projectId,
  });
}

export function useProjectCollaborators(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-collaborators', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const { data, error } = await supabase
        .rpc('get_project_collaborators', { _project_id: projectId });
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        id: row.id,
        name: row.full_name || '',
        email: row.email || '',
      }));
    },
    enabled: !!projectId,
  });
}

export function useSyncProjectCollaborators() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, userIds }: { projectId: string; userIds: string[] }) => {
      // Delete existing
      await supabase.from('project_collaborators').delete().eq('project_id', projectId);
      // Insert new
      if (userIds.length > 0) {
        const rows = userIds.map(uid => ({ project_id: projectId, user_id: uid }));
        const { error } = await supabase.from('project_collaborators').insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-collaborators', projectId] });
    },
  });
}

export function useSyncProjectTools() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, toolNames }: { projectId: string; toolNames: string[] }) => {
      await supabase.from('project_tools').delete().eq('project_id', projectId);
      if (toolNames.length > 0) {
        const rows = toolNames.map(name => ({ project_id: projectId, tool_name: name }));
        const { error } = await supabase.from('project_tools').insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-tools', projectId] });
    },
  });
}

export function useSearchProfiles(query: string, _organizationId?: string | null) {
  return useQuery({
    queryKey: ['search-profiles', query],
    queryFn: async () => {
      if (!query || query.length < 2) return [];
      const { data, error } = await (supabase as any).rpc('search_profiles_for_collaboration', {
        _query: query.trim(),
        _limit: 10,
      });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; full_name: string; email: string }>;
    },
    enabled: query.length >= 2,
  });
}

// Showcase queries
export function useShowcaseProjects(view: 'cohort' | 'organization' | 'public', cohortId?: string, organizationId?: string | null) {
  return useQuery({
    queryKey: ['showcase-projects', view, cohortId, organizationId],
    queryFn: async () => {
      let q = supabase.from('participant_projects').select('*, profiles:user_id(full_name, email)');
      
      if (view === 'cohort' && cohortId) {
        q = q.eq('cohort_id', cohortId);
      } else if (view === 'organization' && organizationId) {
        // Get user IDs from the same organization
        const { data: orgProfiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('organization_id', organizationId);
        const orgUserIds = (orgProfiles ?? []).map(p => p.id);
        if (orgUserIds.length === 0) return [];
        q = q.in('user_id', orgUserIds).eq('is_org_visible', true);
      } else if (view === 'public') {
        q = q.eq('is_public', true);
      } else {
        return [];
      }
      
      q = q.order('created_at', { ascending: false });
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: view === 'public' || (view === 'cohort' && !!cohortId) || (view === 'organization' && !!organizationId),
  });
}

export function useCreateProject() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project: {
      name: string;
      problem?: string;
      target_audience?: string;
      data_used?: string;
      solution_description?: string;
      solution_type?: number;
      kpi_name?: string;
      kpi_current_value?: string;
      kpi_target_value?: string;
      cohort_id?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('participant_projects')
        .insert({ ...project, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (user) {
        logAuditAction(user.id, 'project_create', `Proyecto creado: ${data.name}`, { project_id: data.id } as any);
      }
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ProjectRow> & { id: string }) => {
      const { data, error } = await supabase
        .from('participant_projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', data.id] });
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) logAuditAction(user.id, 'project_update', `Proyecto actualizado: ${data.name}`, { project_id: data.id } as any);
      });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('participant_projects')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
